import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { setClient } from "../../store/slices/clientsSlice";
import ClientModal from "../../modals/ClientModal";
import {
  CalendarIcon,
  PhoneIcon,
  BellAlertIcon,
  DocumentCheckIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CreditCardIcon as CreditCardIconSolid
} from "@heroicons/react/24/outline";

export default function FrontDeskRecallItem({ client }) {
  const dispatch = useDispatch();
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);

  const handleActionClick = () => {
    dispatch(setClient(client));
    setIsClientModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsClientModalOpen(false);
  };

  const getRecallDate = () => {
    const latestRecallStatus = client.statusHistory
      ?.filter((status) => status.status === "recall")
      .sort((a, b) => b.changedAt - a.changedAt)[0];
    return latestRecallStatus?.recallDate;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "short",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const getTimeIndicator = (dateString) => {
    const recallDate = new Date(dateString);
    const now = new Date();
    const diffHours = (recallDate - now) / (1000 * 60 * 60);
    const diffDays = Math.abs(Math.floor(diffHours / 24));

    if (diffHours < 0) {
      return <span className="text-red-600 font-medium">{diffDays} {diffDays === 1 ? 'day' : 'days'} overdue</span>;
    } else if (diffHours < 24) {
      return <span className="text-red-500 font-medium">Today</span>;
    } else if (diffHours < 48) {
      return <span className="text-amber-500 font-medium">Tomorrow</span>;
    } else if (diffHours < 168) { // Within a week
      return <span className="text-blue-500">{diffDays} {diffDays === 1 ? 'day' : 'days'} away</span>;
    } else {
      return <span className="text-gray-500">{diffDays} {diffDays === 1 ? 'day' : 'days'} away</span>;
    }
  };

  const recallDate = getRecallDate();

  return (
    <>
      <div
        className="rounded-sm shadow-sm border p-4 mb-3 hover:shadow-md transition-shadow duration-200 cursor-pointer bg-purple-50 border-purple-100"
        onClick={handleActionClick}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3 mt-1">
            <BellAlertIcon className="h-5 w-5 text-purple-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium truncate text-purple-900">
                  {client.firstName} {client.lastName}
                  {client.preferredName && ` (${client.preferredName})`}
                </h3>
                {client.clinic?.name && (
                  <p className="text-xs text-gray-500 truncate">
                    {client.clinic.name}
                  </p>
                )}
              </div>
              <div className="ml-2 flex-shrink-0">
                {recallDate && getTimeIndicator(recallDate)}
              </div>
            </div>

            <div className="mt-1 flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500">
                <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                <p>{recallDate ? formatDateTime(recallDate) : "No recall date set"}</p>
              </div>
              <div className="flex items-center gap-2">
                {client.contract?.signed ? (
                  <DocumentCheckIcon className="h-4 w-4 text-green-500" title="Contract Signed" />
                ) : (
                  <DocumentTextIcon className="h-4 w-4 text-red-500" title="Contract Not Signed" />
                )}
                {client.creditCardOnFile ? (
                  <CreditCardIconSolid className="h-4 w-4 text-green-500" title="Credit Card on File" />
                ) : (
                  <CreditCardIcon className="h-4 w-4 text-red-500" title="No Credit Card on File" />
                )}
              </div>
            </div>

            <div className="mt-1 text-sm text-gray-500 flex items-center justify-between">
              <p className="flex items-center">
                <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                {client.phoneNumbers?.[0]?.number || "No phone number"}
              </p>
              <span className="text-xs text-gray-500">
                {client.defaultPaymentMethod && client.defaultPaymentMethod.name ? (
                  <>
                    Default: {client.defaultPaymentMethod.name}
                    {client.defaultPaymentMethod.lastFourDigits &&
                      ` (${client.defaultPaymentMethod.lastFourDigits})`}
                  </>
                ) : (
                  "Not assigned"
                )}
              </span>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center text-xs text-purple-600">
                <PhoneIcon className="h-4 w-4 mr-1" />
                <span>Call client to schedule appointment</span>
              </div>

              <button
                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 text-purple-700 bg-purple-100 hover:bg-purple-200"
              >
                Contact Client
              </button>
            </div>
          </div>
        </div>
      </div>

      {isClientModalOpen && (
        <ClientModal
          isOpen={isClientModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
